/**
 * Trend Weight + 7-day delta primitives (BW18 PR-F).
 *
 * Trend Weight is an EWMA over 14 days of body_measurement.weight_kg with
 * a 5-day half-life. Smooths daily scale noise so the chart shows direction,
 * not water weight wobble. Same pattern MacroFactor pirated from a paper on
 * weight self-tracking adherence.
 */

const TREND_WEIGHT_SMOOTHING = 0.13; // 1 - exp(-1 / log(2) * 5) ≈ half-life of 5 days

export type WeightSample = { date: string; weight_kg: number };
export type TrendWeightPoint = { date: string; weight_kg: number; trend_kg: number };

/**
 * Returns one row per input date with both raw weight and EWMA trend. Days
 * without a scale reading carry forward the previous trend (per the brief).
 */
export function computeTrendWeight(samples: WeightSample[]): TrendWeightPoint[] {
  if (samples.length === 0) return [];
  const sorted = [...samples].sort((a, b) => a.date.localeCompare(b.date));

  let trend = sorted[0].weight_kg;
  const out: TrendWeightPoint[] = [];
  for (const s of sorted) {
    trend = TREND_WEIGHT_SMOOTHING * s.weight_kg + (1 - TREND_WEIGHT_SMOOTHING) * trend;
    out.push({
      date: s.date,
      weight_kg: s.weight_kg,
      trend_kg: Math.round(trend * 100) / 100,
    });
  }
  return out;
}

export type DeltaResult = {
  current: number | null;
  avg_7d: number | null;
  avg_28d: number | null;
  delta_7d: number | null;
  delta_28d: number | null;
};

/**
 * Generic delta calculator. `samples` should be in chronological order
 * (oldest first). The most recent value is `current`. 7-day and 28-day
 * averages are computed as means of the trailing window EXCLUDING the
 * current value (so today's reading isn't compared to itself).
 */
export function computeDelta(samples: number[]): DeltaResult {
  if (samples.length === 0) {
    return {
      current: null,
      avg_7d: null,
      avg_28d: null,
      delta_7d: null,
      delta_28d: null,
    };
  }
  const current = samples[samples.length - 1];
  const trailing = samples.slice(0, -1);
  const last7 = trailing.slice(-7);
  const last28 = trailing.slice(-28);

  const avg = (xs: number[]) =>
    xs.length === 0 ? null : xs.reduce((a, b) => a + b, 0) / xs.length;

  const avg_7d = avg(last7);
  const avg_28d = avg(last28);

  return {
    current,
    avg_7d: avg_7d != null ? Math.round(avg_7d * 100) / 100 : null,
    avg_28d: avg_28d != null ? Math.round(avg_28d * 100) / 100 : null,
    delta_7d: avg_7d != null ? Math.round((current - avg_7d) * 100) / 100 : null,
    delta_28d: avg_28d != null ? Math.round((current - avg_28d) * 100) / 100 : null,
  };
}

/**
 * Format a delta for display under a vitals card. Uses ↑ / ↓ glyphs with
 * the unit suffix. Color is intentionally muted (observation, not judgment).
 */
export function formatDelta(delta: number | null, unit: string): string | null {
  if (delta == null || Number.isNaN(delta)) return null;
  const abs = Math.abs(delta);
  // Suppress sub-precision noise that reads as "↑0g" for already-rounded inputs.
  if (abs < 0.01) return `flat vs 7-day avg`;
  const arrow = delta >= 0 ? "↑" : "↓";
  const formatted = abs >= 100 ? Math.round(abs) : Math.round(abs * 10) / 10;
  return `${arrow}${formatted}${unit} vs 7-day avg`;
}
